/* eslint-disable no-console -- this is the place we allow console logging */
import type {Metadata} from "@khanacademy/wonder-stuff-core";

import {distillErrorMessage} from "./distill-error-message.ts";
import Features from "./features.ts";
import type {KAError} from "./ka-error.ts";
import {captureException} from "./khan-sentry.ts";
import type {IKAErrorLog} from "./types.ts";

/**
 * A log implementation that sends log messages to the console and, if an error,
 * to Sentry as needed.
 */
export class ConsoleLog implements IKAErrorLog {
    /**
     * Log a message and optional metadata.
     *
     * @param {string} message The message to log.
     * @param {Metadata} [metadata] Metadata about the logged message.
     */
    log: (
        message: string,
        metadata?: Metadata | null | undefined,
        ...args: Array<unknown>
    ) => void = (message, metadata, ...args): void => {
        if (!Features.logging) {
            // Logging is not enabled so, we noop.
            return;
        }

        if (metadata != null) {
            console.log(message, metadata, ...args);
        } else {
            console.log(message, ...args);
        }

        /**
         * Sentry
         *
         * Regular logging does not go to sentry; only, errors.
         */
    };

    /**
     * Log an error and optional metadata.
     *
     * This logs to sentry also. However, only metadata in the tags and extras
     * sections will be logged to sentry.
     *
     * @param {KAError} error The error to be logged.
     */
    error: (error: KAError) => void = (error: KAError): void => {
        /**
         * We always log errors to the console. These are useful for support.
         */
        const errorMessage = distillErrorMessage(error);
        const data = {
            error,
        };
        console.error(errorMessage, data);
        // NOTE: No need to call MobileApp.Dev.error(errorMessage, data);
        // The sentry configuration sets up an integration to handle that.

        /**
         * Sentry
         */
        captureException(error);
    };
}
